import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell, LabelList, ResponsiveContainer } from 'recharts';
import { getGPS, getPlayers, resolveTargetsBatch } from '../../api';
import { useUrlParam } from '../../hooks/useUrlParam';
import {
  METRICS, COLORS, getStatus, getDeviation, deviationColor, toISOWeekStr, getWeekRange,
} from './individualTargetsShared';

// METRICS/COLORS/getStatus/getDeviation/deviationColor/the ISO-week helpers/getWeekRange moved to
// individualTargetsShared.js (2026-09-10) so IndividualTargetsPanel.js (the Einstein compose
// panel) reuses the exact same target-deviation math instead of a second hand-copy — the same
// math einsteinRouter.js's resolveParamValue also now depends on via resolvers.js's
// toISOWeekString (a separate, deliberately non-cross-directory-shared copy — see that function's
// header comment for why services/ doesn't import from src/). Behavior here is unchanged.
const fmtDate = d => { if (!d) return '—'; const [y,m,day]=d.split('-'); return `${day}/${m}/${y}`; };

function buildWeekOptions() {
  const seen = new Set(); const opts = [];
  const today = new Date();
  for (let i = 8; i >= -52; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() + i * 7);
    const s = toISOWeekStr(d);
    if (!seen.has(s)) { seen.add(s); opts.push(s); }
  }
  return opts;
}
const WEEK_OPTIONS = buildWeekOptions();

// ── Gauge (same arc logic as GPSData.js) ──────────────────────────────────────
function Gauge({ label, value, targets, category }) {
  const v = value ?? 0;
  const status = getStatus(v, targets);
  const color  = COLORS[status];
  const mid    = targets && targets.upper > 0 ? (targets.upper + targets.lower) / 2 || targets.upper : null;
  const pct    = mid ? Math.round((v / mid) * 100) : null;
  const R = 52; const cx = 70; const cy = 70;
  const startDeg = -200; const sweepDeg = 220;
  const arcPath = f => {
    const c = Math.min(Math.max(f, 0), 1);
    const endDeg = startDeg + sweepDeg * c;
    const toR = d => (d * Math.PI) / 180;
    const x1 = cx + R * Math.cos(toR(startDeg)); const y1 = cy + R * Math.sin(toR(startDeg));
    const x2 = cx + R * Math.cos(toR(endDeg));   const y2 = cy + R * Math.sin(toR(endDeg));
    return `M ${x1} ${y1} A ${R} ${R} 0 ${sweepDeg * c > 180 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const fill   = targets && targets.upper > 0 ? Math.min(v / targets.upper, 1.15) : 0;
  const lowerF = targets && targets.upper > 0 ? targets.lower / targets.upper : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 120 }}>
      <svg width="140" height="105" viewBox="0 0 140 105" style={{ overflow: 'visible' }}>
        <path d={arcPath(1)} fill="none" stroke="var(--gauge-track)" strokeWidth="9" strokeLinecap="round" />
        {lowerF != null && (
          <path d={arcPath(lowerF)} fill="none"
            stroke={status === 'none' ? 'var(--gauge-track)' : color}
            strokeWidth="9" strokeLinecap="round" strokeOpacity="0.25" />
        )}
        <path d={arcPath(Math.min(fill, 1))} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" />
        {fill > 1 && (
          <path d={arcPath(fill)} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeOpacity="0.4" />
        )}
        <text x="70" y="66" textAnchor="middle" fill={color} fontSize="17" fontWeight="700" fontFamily="Inter, sans-serif">
          {v > 0 ? Math.round(v).toLocaleString() : '—'}
        </text>
        {pct != null && (
          <text x="70" y="81" textAnchor="middle" fill="var(--text-muted)" fontSize="10" fontFamily="Inter, sans-serif">{pct}%</text>
        )}
      </svg>
      <div style={{ textAlign: 'center', marginTop: -8 }}>
        <div style={{ fontSize: '0.73rem', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</div>
        {targets && targets.upper > 0 ? (
          <div style={{ fontSize: '0.65rem', color: 'var(--text-dimmed)', marginTop: 1 }}>
            {Math.round(targets.lower).toLocaleString()}–{Math.round(targets.upper).toLocaleString()}
          </div>
        ) : (
          <div style={{ fontSize: '0.65rem', color: 'var(--text-dimmed)', marginTop: 1 }}>no target</div>
        )}
      </div>
    </div>
  );
}

// ── Custom bar chart X-axis tick: title on top line, date below ───────────────
function BarXTick({ x, y, payload }) {
  const [title, date] = (payload?.value || '').split('\n');
  return (
    <g transform={`translate(${x},${y + 6})`}>
      <text textAnchor="middle" fill="var(--text-secondary)" fontSize={10} fontFamily="Inter, sans-serif" dy={0}>{title}</text>
      <text textAnchor="middle" fill="var(--text-dimmed)"   fontSize={9}  fontFamily="Inter, sans-serif" dy={13}>{date}</text>
    </g>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function IndividualTargets({ playerOverride = null }) {
  const [players, setPlayers]           = useState([]);
  const [sessions, setSessions]         = useState([]);
  const [playerId, setPlayerId]   = useUrlParam('player_id', playerOverride ? String(playerOverride.id) : '');
  const [isoWeek, setIsoWeek]     = useUrlParam('iso_week', toISOWeekStr(new Date()));
  const [drillTitle, setDrillTitle] = useState('Entire Session - Live');
  const [chartMetric, setChartMetric] = useState('td');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([getGPS(), getPlayers()])
      .then(([gps, pl]) => {
        setSessions(gps || []);
        const sorted = (pl || [])
          .filter(p => p.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPlayers(sorted);
        // Bug fix (2026-09-03): used to default to the first sorted player UNCONDITIONALLY,
        // discarding a `player_id` already in the URL — see PlayerChronic.js's identical fix
        // (window.location.search read directly, not the closure-captured useUrlParam binding —
        // that alone still showed the wrong player live; see that file's comment for why).
        const urlPlayerId = new URLSearchParams(window.location.search).get('player_id');
        if (playerOverride) setPlayerId(String(playerOverride.id));
        else if (!urlPlayerId && sorted.length) setPlayerId(String(sorted[0].id));
      })
      .finally(() => setLoading(false));
  }, [playerOverride]);

  const { start: weekStart, end: weekEnd } = useMemo(() => getWeekRange(isoWeek), [isoWeek]);

  // Which of the week's sessions actually include this player — the raw material both the
  // resolve effect and the final playerWeekSessions memo need.
  const weekSessionsForPlayer = useMemo(() => {
    if (!playerId) return [];
    return sessions
      .filter(s => {
        const d = (s.session_date || '').slice(0, 10);
        return d >= weekStart && d <= weekEnd;
      })
      .flatMap(s => {
        const pd = (s.players || []).find(p => String(p.player_id) === playerId);
        return pd ? [{ session: s, pd }] : [];
      });
  }, [sessions, playerId, weekStart, weekEnd]);

  // Individualized target resolution — batched through /api/targets/resolve-batch
  // (targetResolution.js, backend). This page was previously the most broken target consumer in
  // the app: `assignment = scenarios.find(sc => sc.scenario === scenario)` had NO player_id
  // filter at all, and applied no intensity/MD scaling whatsoever — despite the page's name.
  const [resolvedBySession, setResolvedBySession] = useState(new Map());
  useEffect(() => {
    if (!weekSessionsForPlayer.length) { setResolvedBySession(new Map()); return; }
    const items = weekSessionsForPlayer.map(({ session, pd }) => ({
      key: String(session.id), playerId, position: pd.position || null,
      scenario: session.scenario || '', date: (session.session_date || '').slice(0, 10), dayLabel: pd.md_label || '',
    }));
    let cancelled = false;
    resolveTargetsBatch(items)
      .then(({ results }) => { if (!cancelled) setResolvedBySession(new Map(results.map(r => [r.key, r]))); })
      .catch(err => { console.error('Failed to resolve targets:', err); if (!cancelled) setResolvedBySession(new Map()); });
    return () => { cancelled = true; };
  }, [weekSessionsForPlayer, playerId]);

  const playerWeekSessions = useMemo(() => {
    return weekSessionsForPlayer
      .map(({ session: s, pd }) => {
        const n = v => parseFloat(v) || 0;
        const teamPlayers = (s.players || []).filter(p => p.td != null);
        const avg = key => teamPlayers.length > 0
          ? teamPlayers.reduce((sum, p) => sum + (parseFloat(p[key]) || 0), 0) / teamPlayers.length
          : 0;
        const sessionDate = (s.session_date || '').slice(0, 10);
        const resolved = resolvedBySession.get(String(s.id));
        return {
          sessionId: s.id,
          date: sessionDate,
          title:    s.session_title || '',
          scenario: s.scenario || '', mdLabel: pd.md_label || '', position: pd.position || '',
          targets: resolved?.targets || null,
          td: n(pd.td), rd: n(pd.rd), hid: n(pd.hid),
          sd: n(pd.sd), sa: n(pd.sa), expl: n(pd.expl), hmld: n(pd.hmld),
          teamAvg: { td: avg('td'), rd: avg('rd'), hid: avg('hid'), sd: avg('sd'), sa: avg('sa'), expl: avg('expl'), hmld: avg('hmld') },
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [weekSessionsForPlayer, resolvedBySession]);

  // Cumulative totals: sum actuals + sum each session's day-target across all week sessions
  const cumulative = useMemo(() => {
    if (!playerWeekSessions.length) return null;
    const actual = {}, tgtLower = {}, tgtUpper = {}, teamAvgSum = {};
    METRICS.forEach(m => { actual[m.key] = 0; tgtLower[m.key] = 0; tgtUpper[m.key] = 0; teamAvgSum[m.key] = 0; });
    playerWeekSessions.forEach(s => {
      const tgt = s.targets;
      METRICS.forEach(m => {
        actual[m.key]      += s[m.key] || 0;
        teamAvgSum[m.key]  += s.teamAvg?.[m.key] || 0;
        if (tgt?.[m.tKey]) { tgtLower[m.key] += tgt[m.tKey].lower; tgtUpper[m.key] += tgt[m.tKey].upper; }
      });
    });
    const targets = {};
    METRICS.forEach(m => { targets[m.tKey] = { lower: tgtLower[m.key], upper: tgtUpper[m.key] }; });
    const first = playerWeekSessions[0];
    const last  = playerWeekSessions[playerWeekSessions.length - 1];
    return { actual, targets, teamAvg: teamAvgSum, scenario: first.scenario, position: first.position,
      dateRange: `${fmtDate(first.date)} – ${fmtDate(last.date)}`, sessionCount: playerWeekSessions.length };
  }, [playerWeekSessions]);

  // Per-session bar chart: each bar = that day's actual vs that day's individual target
  const chartData = useMemo(() => {
    const m = METRICS.find(x => x.key === chartMetric);
    if (!m) return [];
    return playerWeekSessions.map(s => {
      const tgt = s.targets?.[m.tKey];
      const dev = tgt ? getDeviation(s[m.key], tgt.lower, tgt.upper) : null;
      const shortDate = s.date ? `${s.date.slice(8)}-${s.date.slice(5,7)}` : '';
      return { label: `${s.title || s.mdLabel}\n${shortDate}`, dev, color: deviationColor(dev) };
    });
  }, [playerWeekSessions, chartMetric]);

  // Remaining = cumulative lower target minus cumulative actual (floored at 0)
  const remainingLoad = useMemo(() => {
    if (!cumulative) return [];
    return METRICS.map(m => {
      const t   = cumulative.targets[m.tKey];
      const val = cumulative.actual[m.key];
      if (!t || t.lower === 0) return { ...m, remaining: null, done: false };
      const rem = Math.max(0, t.lower - val);
      return { ...m, remaining: rem, done: rem === 0 };
    });
  }, [cumulative]);

  const player = useMemo(() => players.find(p => String(p.id) === playerId), [players, playerId]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</div>
  );

  const hasData = playerWeekSessions.length > 0;
  const weekNum = isoWeek.split('-W')[1];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Individual Target Analysis</h2>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>Weekly performance vs. individual session targets</p>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {!playerOverride && (
          <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={SEL}>
            {players.map(p => <option key={p.id} value={String(p.id)}>{p.name || p.username || `Player ${p.id}`}</option>)}
          </select>
        )}
        <select value={isoWeek} onChange={e => setIsoWeek(e.target.value)} style={SEL}>
          {WEEK_OPTIONS.map(w => <option key={w} value={w}>{w.replace('-W', ' · W')}</option>)}
        </select>
        <select value={drillTitle} onChange={e => setDrillTitle(e.target.value)} style={SEL}>
          <option value="Entire Session - Live">Entire Session - Live</option>
        </select>
      </div>

      {/* ── Empty state ── */}
      {!hasData && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, color: 'var(--text-muted)', textAlign: 'center', gap: 12 }}>
          <svg width="44" height="44" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2} opacity={0.4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            No sessions found for this player in week {weekNum}.
          </p>
        </div>
      )}

      {hasData && cumulative && (
        <>
          {/* ── Week summary label ── */}
          <div style={{ marginBottom: 16, padding: '8px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 9, display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Cumulative week total:</span>
            <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {cumulative.sessionCount} session{cumulative.sessionCount !== 1 ? 's' : ''} · {cumulative.dateRange}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-dimmed)' }}>
              {player?.position || cumulative.position} · Scenario {cumulative.scenario}
            </span>
          </div>

          {/* ── Section 1: Gauges (cumulative actual vs cumulative target) ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'nowrap', overflowX: 'auto' }}>
              {METRICS.map(m => (
                <Gauge key={m.key} label={m.label} value={cumulative.actual[m.key]} targets={cumulative.targets[m.tKey]} category={m.category} />
              ))}
            </div>
          </div>

          {/* ── Sections 2 + 3: Chart + Table ── */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>

            {/* Section 2: Deviation bar chart */}
            <div style={{ flex: '2 1 360px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>Daily Deviation</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>per session vs daily target</span>
                </div>
                <select value={chartMetric} onChange={e => setChartMetric(e.target.value)}
                  style={{ ...SEL, minWidth: 0, padding: '4px 28px 4px 10px', fontSize: '0.78rem' }}>
                  {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} margin={{ top: 22, right: 12, left: -8, bottom: 36 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={<BarXTick />} axisLine={false} tickLine={false} interval={0} height={44} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                      axisLine={false} tickLine={false} tickFormatter={v => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`} />
                    <ReferenceLine y={0} stroke="var(--border-strong)" strokeWidth={1.5} />
                    <Tooltip
                      contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                      formatter={v => [v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '—', 'Deviation']}
                    />
                    <Bar dataKey="dev" radius={[3, 3, 0, 0]} maxBarSize={52}>
                      {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                      <LabelList dataKey="dev" content={({ x, y, width, value, height }) => {
                        if (value == null) return null;
                        const lbl = `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
                        const above = value >= 0;
                        const h = Math.abs(height || 0);
                        return (
                          <text x={x + width / 2} y={above ? y - 4 : y + h + 11}
                            textAnchor="middle" fill="var(--text-secondary)" fontSize={10} fontWeight="600"
                            fontFamily="Inter, sans-serif">{lbl}</text>
                        );
                      }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dimmed)', fontSize: '0.83rem' }}>
                  No target data for this metric
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, fontSize: '0.71rem', color: 'var(--text-muted)' }}>
                {[['#10b981','On target'],['#f59e0b','Near (0–10% below)'],['#ef4444','Too low (>10% below)'],['#7c3aed','Overloaded']].map(([c,l]) => (
                  <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: 'inline-block', flexShrink: 0 }} />{l}
                  </span>
                ))}
              </div>
            </div>

            {/* Section 3: Target table */}
            <div style={{ flex: '1 1 280px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Cumulative Week Total</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Metric','Target','Team Avg','Actual',''].map(h => (
                      <th key={h} style={TH}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map((m, i) => {
                    const t    = cumulative.targets[m.tKey];
                    const val  = cumulative.actual[m.key];
                    const avg  = cumulative.teamAvg?.[m.key];
                    const status = getStatus(val, t);
                    const col  = COLORS[status];
                    const hasTgt = t && (t.upper > 0 || t.lower > 0);
                    const icon = status === 'above' ? '↑' : status === 'green' ? '✓' : status === 'amber' ? '↓' : status === 'below' ? '↓' : '—';
                    const iconCol = status === 'above' ? COLORS.above : status === 'green' ? COLORS.green : status === 'amber' ? COLORS.amber : status === 'below' ? COLORS.below : 'var(--text-dimmed)';
                    return (
                      <tr key={m.key} style={{ background: i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent' }}>
                        <td style={{ ...TD, fontSize: '0.75rem' }}>{m.label}</td>
                        <td style={{ ...TD, color: 'var(--text-muted)', fontSize: '0.74rem' }}>
                          {hasTgt ? `${Math.round(t.lower).toLocaleString()}–${Math.round(t.upper).toLocaleString()}` : '—'}
                        </td>
                        <td style={{ ...TD, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                          {avg != null ? Math.round(avg).toLocaleString() : '—'}
                        </td>
                        <td style={{ ...TD, fontWeight: 600, color: col }}>{Math.round(val).toLocaleString()}</td>
                        <td style={{ ...TD, textAlign: 'center', fontWeight: 700, color: iconCol, fontSize: '0.9rem' }}>{icon}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Section 4: Remaining load ── */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px' }}>
            <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Remaining Load Needed</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Metric','Lower Target','Actual','Remaining','Status'].map(h => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {remainingLoad.map((m, i) => {
                  const t   = cumulative.targets[m.tKey];
                  const val = cumulative.actual[m.key];
                  return (
                    <tr key={m.key} style={{ background: i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent' }}>
                      <td style={TD}>{m.label}</td>
                      <td style={{ ...TD, color: 'var(--text-muted)' }}>
                        {t && t.lower > 0 ? Math.round(t.lower).toLocaleString() : '—'}
                      </td>
                      <td style={{ ...TD, fontWeight: 600 }}>{Math.round(val).toLocaleString()}</td>
                      <td style={{ ...TD, fontWeight: 600 }}>
                        {m.remaining === null ? '—' : m.done ? '—' : Math.round(m.remaining).toLocaleString()}
                      </td>
                      <td style={TD}>
                        {m.remaining === null ? (
                          <span style={{ color: 'var(--text-dimmed)' }}>—</span>
                        ) : m.done ? (
                          <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.82rem' }}>✓ Done</span>
                        ) : (
                          <span style={{ color: '#ef4444', fontSize: '0.78rem' }}>+{Math.round(m.remaining).toLocaleString()} needed</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const SEL = {
  padding: '8px 32px 8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.88rem',
  fontFamily: 'inherit', cursor: 'pointer', outline: 'none', minWidth: 190,
  appearance: 'none', WebkitAppearance: 'none',
};
const TH = {
  padding: '8px 10px', fontSize: '0.67rem', fontWeight: 600, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'left',
  borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};
const TD = {
  padding: '8px 10px', fontSize: '0.82rem', color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border)',
};
